import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { Button, ErrorState, Input, Spinner } from '@helios/ui';
import { ConstructionOsLogo } from '@/components/brand/ConstructionOsLogo';
import { useAuth, type AuthContextValue } from './authContext';
import { accessTokenIdentity } from '@/services/authSessionClaims';
import { getAccessToken } from '@/services/authTokenStorage';
import { getSessionOriginKey, isCurrentSessionOrigin } from '@/services/sessionOrigin';
import { explicitAuthReturnPath, type AuthReturnLocation } from './postSignInPath';

/** Restricted sessions never mount the staff shell or its data queries. */
export function PasswordChangePage() {
  const auth = useAuth();
  const location = useLocation();
  const from = (location.state as { from?: AuthReturnLocation } | null)?.from;
  if (auth.isLoading) return <main className="grid min-h-screen place-items-center bg-background"><Spinner /></main>;
  if (!auth.authenticated) return <Navigate to="/auth" replace state={{ from }} />;
  const origin = getSessionOriginKey();
  const claims: unknown = JSON.parse(accessTokenIdentity(getAccessToken()) ?? 'null');
  const matches = Array.isArray(claims) && claims[0] === auth.user?.id && claims[1] === auth.tenant?.id
    && (auth.sessionId === undefined || claims[2] === auth.sessionId)
    && (auth.authVersion === undefined || claims[3] === auth.authVersion)
    && claims[4] === (auth.accessMode === 'PASSWORD_CHANGE_REQUIRED' ? 'PASSWORD_CHANGE' : 'NORMAL');
  if (!origin || !matches) return <main className="grid min-h-screen place-items-center bg-background p-4"><ErrorState title="Sign-in changed" description="Sign in again before changing your password." /></main>;
  return <PasswordChangeForm key={origin} auth={auth} origin={origin} from={from} />;
}

function PasswordChangeForm({ auth, origin, from }: { auth: AuthContextValue; origin: string; from?: AuthReturnLocation }) {
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const restricted = auth.accessMode === 'PASSWORD_CHANGE_REQUIRED';

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!isCurrentSessionOrigin(origin)) return;
    setError(null);
    if (newPassword.length < 15 || newPassword.length > 128) {
      setError('Use a password between 15 and 128 characters.'); return;
    }
    if (newPassword !== confirmation) { setError('The new passwords do not match.'); return; }
    setPending(true);
    try {
      const next = await auth.completePasswordChange({ newPassword, ...(restricted ? {} : { currentPassword }) });
      const currentClaims: unknown = JSON.parse(accessTokenIdentity(getAccessToken()) ?? 'null');
      if (!next.authenticated || next.accessMode !== 'FULL' || next.user?.id !== auth.user?.id || next.tenant?.id !== auth.tenant?.id
        || !Array.isArray(currentClaims) || currentClaims[0] !== next.user?.id || currentClaims[1] !== next.tenant?.id
        || (next.sessionId !== undefined && currentClaims[2] !== next.sessionId)
        || (next.authVersion !== undefined && currentClaims[3] !== next.authVersion) || currentClaims[4] !== 'NORMAL') return;
      if (mounted.current) { setCurrentPassword(''); setNewPassword(''); setConfirmation(''); }
      navigate('/auth', { replace: true, state: explicitAuthReturnPath(from) ? { from } : null });
    } catch (caught) {
      if (mounted.current && isCurrentSessionOrigin(origin)) setError(caught instanceof Error ? caught.message : 'Password change failed.');
    } finally { if (mounted.current && isCurrentSessionOrigin(origin)) setPending(false); }
  };

  return (
    <main className="flex min-h-screen items-center justify-center overflow-y-auto bg-background p-4 text-foreground">
      <section className="w-full max-w-md space-y-5 rounded-lg border border-border bg-card p-5" aria-labelledby="password-change-title">
        <ConstructionOsLogo variant="auth" />
        <div>
          <h1 id="password-change-title" className="text-lg font-semibold">{restricted ? 'Set your permanent password' : 'Change your password'}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{restricted ? 'Complete this step to open your workspace.' : 'Verify your current password and choose a new one.'}</p>
          <p className="mt-2 text-xs text-muted-foreground">{auth.user?.email} · {auth.tenant?.name}</p>
        </div>
        <form className="space-y-4" onSubmit={(event) => void submit(event)}>
          {!restricted ? <div className="space-y-1"><label htmlFor="password-current" className="text-xs">Current password</label><Input id="password-current" type="password" autoComplete="current-password" required maxLength={128} value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} /></div> : null}
          <div className="space-y-1"><label htmlFor="password-new" className="text-xs">New password</label><Input id="password-new" type="password" autoComplete="new-password" required minLength={15} maxLength={128} value={newPassword} onChange={(event) => setNewPassword(event.target.value)} /><p className="text-xs text-muted-foreground">15–128 characters. Spaces are preserved.</p></div>
          <div className="space-y-1"><label htmlFor="password-confirm" className="text-xs">Confirm new password</label><Input id="password-confirm" type="password" autoComplete="new-password" required minLength={15} maxLength={128} value={confirmation} onChange={(event) => setConfirmation(event.target.value)} /></div>
          {error ? <p role="alert" className="text-sm text-critical">{error}</p> : null}
          <div className="flex flex-wrap justify-between gap-2">
            <Button type="button" variant="outline" disabled={pending} onClick={() => { void auth.signOut().then(() => navigate('/auth', { replace: true })); }}>Sign out</Button>
            <Button type="submit" disabled={pending}>{pending ? <Spinner className="h-3.5 w-3.5" /> : null}Save password</Button>
          </div>
        </form>
      </section>
    </main>
  );
}
